import classNames from 'classnames'
import React, { ReactElement, ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useStableId } from '../../utils'
import { FormError } from '../form-error'
import { FormInput } from '../form-input'
import { IconFa } from '../icon-fa'
import { IconLoading } from '../icon-loading'
import styles from './FormSelect.module.css'

type ChildType = ReactElement<{ children: ReactElement<{ label: ReactNode }> }>

const unwrapLabel = (label: ReactNode): ReactNode =>
  React.isValidElement<{ label?: ReactNode }>(label) && label.props.label != null
    ? unwrapLabel(label.props.label)
    : label

const getChildsLabelProp = (node: ReactNode): ReactNode | false =>
  unwrapLabel((node as ChildType)?.props?.children?.props?.label) || false

export interface FormSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  selected?: ReactNode
  openAbove?: boolean
  showInFormStyling?: boolean
  multi?: boolean
  hideChevron?: boolean
  /**
   * Normally the search field will be shown if the number of items is
   * greater than 10. By setting `forceSearchable` to true the search field
   * will be shown anyway. You probably want to set this if you are
   * using `onSearchChange`.
   */
  forceSearchable?: boolean
  isLoading?: boolean
  label?: string
  alignMenu?: 'left' | 'right'
  error?: boolean
  errorMessage?: ReactNode
  buttonClass?: string
  /**
   * If this is set no filtering will happen in this component. The search
   * string is instead passed to this function and the
   * parent component is supposed to handle all filter/search logic and only
   * pass the filtered children.
   *
   * @param searchString
   */
  onSearchChange?: (searchString: string) => void
  onClose?: () => void
  placeholder?: string
  containerClassName?: string
  listClassName?: string
}

export const FormSelect = ({
  children,
  error,
  errorMessage,
  openAbove,
  showInFormStyling,
  selected,
  placeholder,
  isLoading,
  multi = false,
  forceSearchable = false,
  alignMenu = 'left',
  hideChevron,
  onSearchChange,
  onClose,
  containerClassName,
  listClassName,
  ...props
}: FormSelectProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const listRef = useRef<HTMLUListElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [isFocused, setIsFocused] = useState<boolean>(false)
  const [searchable, setSearchable] = useState(forceSearchable)
  const [filter, setFilter] = useState('')
  const errorMessageElId = useId()

  const childrenCount = useMemo(() => React.Children.toArray(children).length, [children])
  useEffect(() => {
    if (forceSearchable || childrenCount > 10) {
      // If forceSearchable or if children at some point is above 10, then always show searchable
      setSearchable(true)
    }
  }, [childrenCount, forceSearchable])

  const filteredChildren = useMemo(() => {
    // if we have an external filter, or it isn't searchable, don't filter anything here
    if (onSearchChange || !searchable) return children

    return React.Children.toArray(children).filter((child) => {
      const label = getChildsLabelProp(child)
      return label ? label.toLocaleString().toLocaleLowerCase().includes(filter.toLocaleLowerCase()) : true
    })
  }, [children, filter, onSearchChange, searchable])

  const handleInputChange = (searchString: string): void => {
    if (onSearchChange) onSearchChange(searchString)
    else setFilter(searchString)
  }

  const toggling = () => {
    setIsOpen(!isOpen)
    handleInputChange('')
    if (isOpen) {
      onClose?.()
    }
  }

  const close = () => {
    setIsOpen(false)
    handleInputChange('')
    onClose?.()
  }

  const closeIfApplicable = (event: MouseEvent) => {
    if (event.detail === 0) {
      // keypress, handled separately
    } else {
      // mouse event
      if (inputRef.current && event.composedPath().includes(inputRef.current)) {
        // dont close if input is selected
      } else if (listRef.current && buttonRef.current) {
        if (event.composedPath().includes(listRef.current)) {
          if (!multi) {
            requestAnimationFrame(() => {
              close()
            })
          }
        } else if (!event.composedPath().includes(buttonRef.current)) {
          close()
        }
      }
    }
  }

  const closeOnValueSelectIfApplicable = (event: KeyboardEvent): any => {
    if (
      listRef.current &&
      event.composedPath().includes(listRef.current) &&
      (event.key === 'Enter' || event.key === 'Escape')
    ) {
      setIsOpen(!isOpen)
      if (event.key === 'Escape') buttonRef.current?.focus()
    }
  }

  useEffect(() => {
    window.addEventListener('click', closeIfApplicable)
    window.addEventListener('keydown', closeOnValueSelectIfApplicable)
    return () => {
      window.removeEventListener('click', closeIfApplicable)
      window.removeEventListener('keydown', closeOnValueSelectIfApplicable)
    }
  }, [closeIfApplicable, closeOnValueSelectIfApplicable])

  const dropdownListId = useStableId()
  const labelId = useStableId()

  const onFocus = () => {
    setIsFocused(true)
  }

  const onBlur = () => {
    setIsFocused(false)
  }

  return (
    <div className={classNames(showInFormStyling && styles['show-in-form'], containerClassName)}>
      {props.label && (
        <label
          className={classNames(styles.labelText, error && styles.error, isFocused && styles.focus)}
          id={labelId}
          aria-hidden={true}
        >
          {props.label}
          {(props['aria-required'] || props.required) && '*'}
        </label>
      )}
      <div
        className={classNames(props.className, styles['form-select'])}
        style={props.style}
        role="combobox"
        aria-labelledby={labelId}
        aria-required={props['aria-required']}
        aria-controls={dropdownListId}
        aria-describedby={errorMessageElId}
        aria-invalid={error}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={props['aria-label']}
      >
        <button
          type="button"
          aria-controls={dropdownListId}
          className={classNames(
            styles['dropdown-button'],
            'hover-aa-supported',
            error && styles.hasError,
            props.buttonClass
          )}
          disabled={props.disabled}
          onFocus={onFocus}
          onBlur={onBlur}
          ref={buttonRef}
          onClick={toggling}
        >
          <span className="text-truncate">{selected || placeholder}</span>
          {hideChevron ? null : <IconFa icon={isOpen ? ['far', 'chevron-up'] : ['far', 'chevron-down']} />}
        </button>
        {isOpen && (
          <DropdownList
            id={dropdownListId}
            openAbove={openAbove}
            alignMenu={alignMenu}
            searchable={searchable}
            onSearchChange={handleInputChange}
            isLoading={isLoading}
            listRef={listRef}
            inputRef={inputRef}
            className={listClassName}
          >
            {filteredChildren}
          </DropdownList>
        )}
      </div>
      <FormError id={errorMessageElId} showError={error} errorMessage={errorMessage} />
    </div>
  )
}

interface DropdownListProps {
  id?: string
  openAbove?: boolean
  alignMenu?: 'left' | 'right'
  listRef?: React.RefObject<HTMLUListElement | null>
  searchable?: boolean
  onSearchChange?: (value: string) => void
  inputRef?: React.RefObject<HTMLInputElement | null>
  isLoading?: boolean
  children: ReactNode
  className?: string
}
export const DropdownList = ({
  id,
  openAbove,
  alignMenu,
  listRef,
  searchable,
  onSearchChange,
  inputRef,
  isLoading,
  children,
  className
}: DropdownListProps) => {
  const key = useStableId()
  const localId = id || key
  const [searchPhrase, setSearchPhrase] = useState('')
  const onChange = (value: string) => {
    onSearchChange?.(value)
    setSearchPhrase(value)
  }

  return (
    <ul
      id={localId}
      className={classNames(
        styles['dropdown-list'],
        'w-full min-w-min',
        'drop-shadow-02',
        openAbove ? styles['position-above'] : null,
        alignMenu === 'right' ? 'right-0' : null,
        className
      )}
      role="listbox"
      ref={listRef}
      key={key}
    >
      {searchable ? (
        <div className={styles.searchable}>
          <FormInput
            hideLabel
            autoFocus
            onClear={() => onChange('')}
            onChange={(e) => onChange((e.target as HTMLInputElement).value)}
            ref={inputRef}
            value={searchPhrase}
            icon={isLoading ? <IconLoading /> : <IconFa icon={['far', 'search']} />}
          />
        </div>
      ) : null}
      {children}
    </ul>
  )
}
